import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Handshake, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatCurrency } from '@/lib/format'
import {
  ConsignmentForm,
  type ConsignmentFormValues,
} from '@/features/consignment/consignment-form'
import { useConsignmentByBoat, useCreateConsignment } from '@/features/consignment/hooks'
import { CONSIGNMENT_STATUS_LABELS, CONSIGNMENT_STATUS_VARIANT } from '@/types/consignment'
import { Badge } from '@/components/ui/badge'
import type { Boat } from '@/types/inventory'

// Consignment slice for the boat detail page: shows the boat's open agreement,
// or offers to consign the boat when it has none.
export function BoatConsignmentCard({ boat }: { boat: Boat }) {
  const { data: consignment, isLoading } = useConsignmentByBoat(boat.id)
  const [createOpen, setCreateOpen] = useState(false)
  const createMutation = useCreateConsignment()

  const handleCreate = async (values: ConsignmentFormValues) => {
    try {
      await createMutation.mutateAsync({
        boat_id: boat.id,
        consignor_id: values.consignor_id,
        commission_rate: values.commission_rate,
        asking_price: values.asking_price,
        agreement_end: values.agreement_end || undefined,
        notes: values.notes || '',
      })
      setCreateOpen(false)
    } catch {
      // createMutation.isError surfaces the message in the dialog.
    }
  }

  if (isLoading) return null

  // Consignments are a used-inventory instrument: on a new boat the option
  // doesn't exist at all (new boats are floor-plan financed instead). An
  // existing agreement still renders so its history stays visible.
  if (!consignment && boat.condition !== 'used') return null

  if (!consignment) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Handshake className="h-4 w-4" /> Consignment
          </CardTitle>
          <CardDescription>
            Marina-owned. Consign it to sell on an owner's behalf for a commission.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <Button variant="outline" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Consign this boat
            </Button>
            <DialogContent className="sm:max-w-xl">
              <DialogHeader>
                <DialogTitle>
                  Consign {boat.make} {boat.model}
                </DialogTitle>
                <DialogDescription>Sign a consignment agreement for this boat.</DialogDescription>
              </DialogHeader>
              <ConsignmentForm
                fixedBoat={boat}
                onSubmit={handleCreate}
                isPending={createMutation.isPending}
                submitLabel="Create Agreement"
              />
              {createMutation.isError && (
                <p className="text-sm text-destructive">
                  Failed to create consignment: {createMutation.error.message}
                </p>
              )}
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    )
  }

  return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-medium">
            <Handshake className="h-4 w-4" /> Consignment
            <Badge variant="outline" className={CONSIGNMENT_STATUS_VARIANT[consignment.status]}>
              {CONSIGNMENT_STATUS_LABELS[consignment.status]}
            </Badge>
          </CardTitle>
        </CardHeader>
      <CardContent className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Asking</span>
          <span>{formatCurrency(consignment.asking_price)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Commission</span>
          <span>{consignment.commission_rate}%</span>
        </div>
        {consignment.sale_price != null && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Sale</span>
            <span>{formatCurrency(consignment.sale_price)}</span>
          </div>
        )}
        {consignment.net_proceeds != null && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Owner proceeds</span>
            <span>{formatCurrency(consignment.net_proceeds)}</span>
          </div>
        )}
        <div className="pt-2">
          <Link
            to="/inventory/consignment/$consignmentId"
            params={{ consignmentId: consignment.id }}
          >
            <Button variant="outline" size="sm">
              View agreement
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}
